/**
 * Local date helpers. CONTRACTS §10: `localDate` is computed on the device at event creation
 * in the device zone and carried in the event. The server never recomputes it, and nothing
 * here ever touches a timezone database.
 */

export type LocalDate = string // 'YYYY-MM-DD'

export const LOCAL_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export function isLocalDate(value: string): value is LocalDate {
  if (!LOCAL_DATE_RE.test(value)) return false
  const parts = value.split('-')
  const y = Number(parts[0])
  const m = Number(parts[1])
  const d = Number(parts[2])
  if (m < 1 || m > 12 || d < 1) return false
  return d <= daysInMonth(y, m)
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

/** The device local date of a timestamp, formatted YYYY-MM-DD. */
export function localDateOf(at: Date | number = new Date()): LocalDate {
  const d = typeof at === 'number' ? new Date(at) : at
  const y = String(d.getFullYear()).padStart(4, '0')
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** The month key of a local date, formatted YYYY-MM (used by parent observations). */
export function monthOf(date: LocalDate): string {
  return date.slice(0, 7)
}

function toUtcMs(date: LocalDate): number {
  const parts = date.split('-')
  return Date.UTC(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
}

function fromUtcMs(ms: number): LocalDate {
  const d = new Date(ms)
  const y = String(d.getUTCFullYear()).padStart(4, '0')
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Calendar arithmetic on the date string only. No local clock, no DST surprises. */
export function addDays(date: LocalDate, days: number): LocalDate {
  return fromUtcMs(toUtcMs(date) + days * 86_400_000)
}

/** Whole days from `a` to `b`, negative when `b` is earlier. */
export function daysBetween(a: LocalDate, b: LocalDate): number {
  return Math.round((toUtcMs(b) - toUtcMs(a)) / 86_400_000)
}

/** Every date from `from` to `to` inclusive, ascending. Empty when `to` is before `from`. */
export function dateRange(from: LocalDate, to: LocalDate): LocalDate[] {
  const out: LocalDate[] = []
  const span = daysBetween(from, to)
  for (let i = 0; i <= span; i++) out.push(addDays(from, i))
  return out
}
